// ============================================================
// District school years — DB-backed with a static fallback.
//
// Districts manage their own evaluation cycles in the school_years
// table (one 'active', plus 'planning' and 'closed' years). Until a
// district has rows there, the static SCHOOL_YEARS list keeps the
// demo/dev experience working.
// ============================================================
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { SCHOOL_YEARS, yearInfo as staticYearInfo } from '../lib/types'
import type { SchoolYearInfo, Phase } from '../lib/types'

interface SchoolYearRow {
  id: string
  district_id: string
  year: string
  label: string
  status: 'active' | 'planning' | 'closed'
  due_fall: string | null
  due_midyear: string | null
  due_spring: string | null
}

export interface UseSchoolYears {
  /** Ordered newest → oldest. */
  years: SchoolYearInfo[]
  /** The district's live evaluation cycle. */
  activeYear: string
  /** Info for any year value (falls back to a parsed label). */
  yearInfo: (value: string) => SchoolYearInfo
  /** District default due date (ISO) for a phase of a year, if set. */
  dueFor: (year: string, phase: Phase) => string | null
}

const DUE_KEY: Record<Phase, keyof Pick<SchoolYearRow, 'due_fall' | 'due_midyear' | 'due_spring'>> = {
  fall: 'due_fall',
  midyear: 'due_midyear',
  spring: 'due_spring',
}

export function useSchoolYears(): UseSchoolYears {
  const { profile } = useAuth()

  const { data: rows = [] } = useQuery<SchoolYearRow[]>({
    queryKey: ['school-years', profile?.district_id],
    queryFn: async () => {
      if (!profile) return []
      const { data } = await supabase
        .from('school_years')
        .select('*')
        .eq('district_id', profile.district_id)
        .order('year', { ascending: false })
      return (data as SchoolYearRow[]) ?? []
    },
    enabled: !!profile,
    staleTime: 5 * 60_000,
  })

  const dbBacked = rows.length > 0
  const years: SchoolYearInfo[] = dbBacked
    ? rows.map((r) => ({ value: r.year, label: r.label, status: r.status }))
    : SCHOOL_YEARS

  const activeYear = years.find((y) => y.status === 'active')?.value ?? years[0]?.value ?? ''

  function yearInfo(value: string): SchoolYearInfo {
    return years.find((y) => y.value === value) ?? staticYearInfo(value)
  }

  function dueFor(year: string, phase: Phase): string | null {
    const row = rows.find((r) => r.year === year)
    return row?.[DUE_KEY[phase]] ?? null
  }

  return { years, activeYear, yearInfo, dueFor }
}
